// Whether a session's report is the deterministic one.
//
// Two conditions, both required, mirroring the backend gate in
// `src/apiserver/utils/session_analysis/deterministic_gate.py`:
//
//   the `deterministic_report_inputs` flag is on for this educator, AND
//   the session started on or after the cutoff.
//
// The flag half is resolved server-side and read through `useFeatureFlag`; the
// date half is per-session, so it is decided here against the row the report is
// rendering. Sessions before the cutoff keep the report they were generated
// with and are never regenerated.
//
// Keeping the cutoff in step with the backend constant is manual: when the date
// moves, it moves in both places or the two halves disagree about the same
// session.

/** Matches DETERMINISTIC_REPORT_CUTOFF in deterministic_gate.py. */
export const DETERMINISTIC_REPORT_CUTOFF = "2026-08-26";

/**
 * Whether `startedAt` falls on or after the cutoff.
 *
 * Compared on calendar date in the viewer's timezone, the same shape as the
 * backend's `started_at.date() >= cutoff`. A session with no start time never
 * qualifies.
 *
 * @param {string|Date|null|undefined} startedAt Session start, ISO or Date.
 * @param {string} cutoff Cutoff date as `YYYY-MM-DD`.
 * @returns {boolean}
 */
export const startedOnOrAfter = (startedAt, cutoff = DETERMINISTIC_REPORT_CUTOFF) => {
  if (!startedAt) return false;

  const started = startedAt instanceof Date ? startedAt : new Date(startedAt);
  if (Number.isNaN(started.getTime())) return false;

  const [y, m, d] = cutoff.split("-").map(Number);
  const cutoffDate = new Date(y, m - 1, d);
  const startedDate = new Date(
    started.getFullYear(),
    started.getMonth(),
    started.getDate(),
  );

  return startedDate >= cutoffDate;
};
